/**
* @summary - Component that Maintenance Calendar Tab and Quick Work Order Popup
* @author Yuvasri S, modified by Yuvasri S
* @since 2024-07-24, modified date (2024-11-08)
*/
import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Typography } from '@mui/material';
import Scheduled from './Scheduled';
import Unscheduled from './Unscheduled';
import PageTitle from '../commoncomponents/PageTitle';
import CustomTextFieldWithSearch from '../commoncomponents/CustomTextFieldWithSearch';
import CustomTextArea from '../commoncomponents/CustomTextArea';
import CustomTextField from '../commoncomponents/CustomTextField';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import { BemsGraph, FemsGraph, MasterGraph } from "../../utility/apiURLS";
import { getAuthToken, getFacilityId } from "../../utility/tokenUtility";
import axios from "axios";
import CustomDialog from "../commoncomponents/CustomDialog";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';

const MaintenanceCalender = () => {
    const [tabValue, setTabValue] = useState(0);
    const [open, setOpen] = useState(false);

    const accessData = useAccess();
    const access = utilities.getScreenPermissions(accessData, "ENGA006");

    const facilityid = getFacilityId();

    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    const GUID_EMPTY = "00000000-0000-0000-0000-000000000000";
    const serviceOptions = [
        { label: "FEMS", value: "FEMS", id: 1 },
        { label: "BEMS", value: "BEMS", id: 2 },
    ];
    const [service, setService] = useState('');

    const [selectedUserDepartment, setSelectedUserDepartment] = useState('');
    const [userDepartmentOptions, setUserDepartmentOptions] = useState([]);
    const [deptDescription, setDeptDescription] = useState('')

    const [workgroupOptions, setWorkgroupOptions] = useState([]);
    const [selectedWorkGroup, setSelectedWorkGroup] = useState('');

    const [assetOptions, setAssetOptions] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState('');
    const [assetDescription, setAssetDescription] = useState('');

    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');

    const [taskCodeOptions, setTaskCodeOptions] = useState([]);
    const [selectedTaskCode, setSelectedTaskCode] = useState('');
    const [taskDescription, setTaskDescription] = useState('');

    const [requestor, setRequestor] = useState('');

    const [requestDetails, setRequestDetails] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);

    const [workOrderNo, setWorkOrderNo] = useState('');

    //cancel validation
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

    const [errors, setErrors] = useState({
        service: false,
        selectedUserDepartment: false,
        selectedAsset: false,
        selectedTaskCode: false,
        selectedWorkGroup: false,
        requestor: false,
        selectedEmployee: false,
        requestDetails: false
    });

    const [errorMessages, setErrorMessages] = useState({
        service: "",
        selectedUserDepartment: "",
        selectedAsset: "",
        selectedTaskCode: "",
        selectedWorkGroup: "",
        requestor: "",
        selectedEmployee: "",
        requestDetails: ""
    });

    const clearForm = () => {
        setErrors({
            service: false,
            selectedUserDepartment: false,
            selectedAsset: false,
            selectedTaskCode: false,
            selectedWorkGroup: false,
            requestor: false,
            selectedEmployee: false,
            requestDetails: false
        });

        setErrorMessages({
            service: "",
            selectedUserDepartment: "",
            selectedAsset: "",
            selectedTaskCode: "",
            selectedWorkGroup: "",
            requestor: "",
            selectedEmployee: "",
            requestDetails: ""
        });

    }

    const fetchAssetRegisters = async (service, workgroupId, deptId) => {
        try {
            const token = getAuthToken("id_token");
            const config = { headers: { Authorization: `Bearer ${token}` } };

            let formattedData = [];

            if (service?.value === "FEMS") {
                // Fetch data from FEMS API only
                const femsApiUrl = `${FemsGraph}/api/GraphFemsAssetregisterlist/GetFemAssetRegisterListFilter/${facilityid}/${workgroupId}/${deptId}`;
                const response = await axios.get(femsApiUrl, config);
                formattedData = response.data.map((asset) => ({
                    id: asset.id,
                    label: asset.assetNo,
                    value: asset.descrption,
                }));
            } else if (service?.value === "BEMS") {
                // Fetch data from BEMS API only
                const bemsApiUrl = `${BemsGraph}/api/GraphBemsAssetregisterlist/GetBemAssetRegisterListFilter/${facilityid}/${workgroupId}/${deptId}`;
                const response = await axios.get(bemsApiUrl, config);
                formattedData = response.data.map((asset) => ({
                    id: asset.id,
                    label: asset.assetNo,
                    value: asset.descrption,
                }));
            } else {
                // Fetch data from both APIs if service value is an empty string
                const femsApiUrl = `${FemsGraph}/api/GraphFemsAssetregisterlist/GetFemAssetRegisterListFilter/${facilityid}/${workgroupId}/${deptId || GUID_EMPTY}`;
                const bemsApiUrl = `${BemsGraph}/api/GraphBemsAssetregisterlist/GetBemAssetRegisterListFilter/${facilityid}/${workgroupId}/${deptId || GUID_EMPTY}`;

                const [femsResponse, bemsResponse] = await Promise.all([
                    axios.get(femsApiUrl, config),
                    axios.get(bemsApiUrl, config)
                ]);

                const femsData = femsResponse.data.map((asset) => ({
                    id: asset.id,
                    label: asset.assetNo,
                    value: asset.descrption,
                }));

                const bemsData = bemsResponse.data.map((asset) => ({
                    id: asset.id,
                    label: asset.assetNo,
                    value: asset.descrption,
                }));

                // Combine FEMS and BEMS data
                formattedData = [...femsData, ...bemsData];
            }

            setAssetOptions(formattedData);
        } catch (error) {
            // Handle error as needed
            // console.error("Error fetching asset registers:", error);
        }
    };

    const fetchUserDepartment = async () => {

        try {

            const token = getAuthToken("id_token");
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };
            const departmentDetailsResponse = await axios.get(
                `${MasterGraph}/api/GraphUserDepartmentDetails/GetUserDepartmentDetailsByFacilityId/${facilityid}?size=1000`,
                config
            );
            const departmentOptions = departmentDetailsResponse.data.items.map(
                (department) => ({
                    value: department.departmentdesc,
                    label: ` ${department.departmentcode} - ${department.departmentdesc} `,
                    id: department.id,
                })
            );
            setUserDepartmentOptions(departmentOptions)
        } catch (error) {
            //   console.error("Error fetching asset type codes:", error);
        }
    };

    const fetchWorkGroupDetails = async (service) => {
        try {
            const token = getAuthToken("id_token");
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const apiUrl = `${service?.value === "FEMS" ? FemsGraph : BemsGraph}/api/WorkGroupDetails/GetWorkGroupDetailsList/${facilityid}?page=1&size=2000`;

            const response = await axios.get(apiUrl, config);
            const formattedData = response.data.items.map((group) => ({
                value: group.wgId,
                label: `${group.wgcode} - ${group.wgdesc}`
            }));

            setWorkgroupOptions(formattedData);


        } catch (error) {
            // console.error("Error fetching workgroup details:", error);
        }
    };

    const fetchEmployeeDetails = async () => {
        const facilityId = getFacilityId('facilityId');
        try {
            const token = getAuthToken("id_token");
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            const response = await axios.get(`${FemsGraph}/api/Femunscheduledworkorder/GetWorkOrderEmployeeDetailsFetchList/${facilityId}`, config);
            const formattedData = response.data.map((item) => ({
                label: `${item?.employeecode || ""} - ${item?.employeename || ""}` || "",
                employeename: item?.employeename || "",
                employeecode: item?.employeecode || "",
                id: item?.id || ""
            }));
            setEmployeeOptions(formattedData)
        } catch (error) {
            console.log(error);
        }
    };

    const fetchTaskCode = async (serviceType, workGroupId) => {
        console.log('serviceType', serviceType)
        const facilityId = getFacilityId('facilityId');
        const checkFEMorBEM = serviceType?.value === "FEMS" ? "Fem" : "Bem";
        const checkGraph = serviceType?.value === "FEMS" ? FemsGraph : BemsGraph;
        try {
            const token = getAuthToken("id_token");
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            const response = await axios.get(`${checkGraph}/api/${checkFEMorBEM}unscheduledworkorder/GetWorkOrderTaskCodeFetchList/${facilityId},${workGroupId},1,100000`, config);
            const formattedData = response.data.map((item) => ({
                label: item?.taskcode,
                value: item?.taskdesc,
                id: item?.id
            }));
            setTaskCodeOptions(formattedData)
        }
        catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        fetchWorkGroupDetails(service);
        fetchAssetRegisters(service, GUID_EMPTY, GUID_EMPTY);
        fetchEmployeeDetails();
        fetchUserDepartment();
    }, []);

    const handleUserDepartmentChange = (newValue) => {

        if (newValue && newValue.value) {
            setSelectedUserDepartment({
                ...newValue,
                label: newValue.label.split('-')[0].trim()
            });
            setDeptDescription(newValue.value);
            setErrors((prevErrors) => ({ ...prevErrors, selectedUserDepartment: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, selectedUserDepartment: "" }));
            fetchAssetRegisters("", GUID_EMPTY, newValue.id);
        } else {
            setSelectedUserDepartment('');
            setDeptDescription("");
            setService("");
            fetchAssetRegisters("", GUID_EMPTY, GUID_EMPTY);
            setSelectedAsset({ label: '', value: '' });
            setSelectedWorkGroup('');
            setAssetDescription('');
            setSelectedTaskCode({ label: '', value: '' });
            setTaskDescription('');
        }
    };

    const handleServiceChange = (newValue) => {

        if (newValue) {
            setService(newValue);
            fetchWorkGroupDetails(newValue);
            fetchAssetRegisters(newValue, GUID_EMPTY, selectedUserDepartment.id);
            fetchTaskCode(newValue.value);
            fetchEmployeeDetails();
            setSelectedAsset({ label: '', value: '' });
            setAssetDescription('');
            setSelectedTaskCode({ label: '', value: '' });
            setTaskDescription('');
            setErrors((prevErrors) => ({ ...prevErrors, service: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, service: "" }));
        }
        else {
            setService('');
            fetchAssetRegisters("", GUID_EMPTY, selectedUserDepartment.id);
            setSelectedAsset({ label: '', value: '' });
            setSelectedWorkGroup('');
            setSelectedTaskCode({ label: '', value: '' });
            setTaskDescription('');
            setAssetDescription('');
        }
    };

    const handleWorkGroupChange = async (selected) => {

        if (selected) {
            setSelectedWorkGroup(selected);
            fetchAssetRegisters(service, selected.value, selectedUserDepartment.id);
            fetchTaskCode(service, selected.value);
            setSelectedAsset({ label: '', value: '' });
            setAssetDescription('');
            setSelectedTaskCode({ label: '', value: '' });
            setTaskDescription('');
            setErrors((prevErrors) => ({ ...prevErrors, selectedWorkGroup: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, selectedWorkGroup: "" }));
        }
        else {
            fetchAssetRegisters(service, GUID_EMPTY, selectedUserDepartment.id);
            setSelectedAsset({ label: '', value: '' });
            setSelectedWorkGroup('');
            setAssetDescription('');
            setSelectedTaskCode({ label: '', value: '' });
            setTaskDescription('');
        }

    };

    const handleAssetChange = (newValue) => {

        if (newValue?.value) {
            const selectedValue = newValue.value;
            const selectedAssetDetails = assetOptions.find(
                (asset) => asset.value === selectedValue
            );

            if (selectedAssetDetails) {
                setSelectedAsset({
                    ...newValue,
                    label: newValue.label.split('-')[0].trim(),
                });
                setAssetDescription(newValue.value);
            }
            setErrors((prevErrors) => ({ ...prevErrors, selectedAsset: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, selectedAsset: "" }));
        } else {
            setSelectedAsset('');
            setAssetDescription('');
            setSelectedTaskCode({ label: '', value: '' });
            setTaskDescription('');
        }
    };

    const handleEmployeeChange = (newValue) => {

        if (newValue?.value) {
            setSelectedEmployee(newValue);
            setErrors((prevErrors) => ({ ...prevErrors, selectedEmployee: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, selectedEmployee: "" }));
        } else {
            setSelectedEmployee('');
        }

    }

    const handleTaskCodeChange = (newValue) => {

        if (newValue) {
            setSelectedTaskCode({
                ...newValue,
                label: newValue.label.split('-')[0].trim()
            });
            setTaskDescription(newValue.value);
            setErrors((prevErrors) => ({ ...prevErrors, selectedTaskCode: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, selectedTaskCode: "" }));
        } else {
            setSelectedTaskCode('');
        }

    }

    const handleRequestorChange = (newValue) => {

        if (newValue) {
            setRequestor(newValue);
            setErrors((prevErrors) => ({ ...prevErrors, requestor: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, requestor: "" }));
        } else {
            setRequestor('');
        }

    }


    const handleRequestDetailsChange = (newValue) => {

        if (newValue) {
            setRequestDetails(newValue);
            setErrors((prevErrors) => ({ ...prevErrors, requestDetails: false }));
            setErrorMessages((prevMessages) => ({ ...prevMessages, requestDetails: "" }));
        } else {
            setRequestDetails('');
        }

    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleCreateNew = () => {
        setOpen(true);

    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleCancel = () => {
        if (
            requestor ||
            selectedUserDepartment ||
            service ||
            selectedAsset.id ||
            selectedTaskCode.length > 0 ||
            selectedEmployee.length > 0 ||
            requestDetails
        ) {
            setCancelDialogOpen(true);
        } else {
            setOpen(false);
            setSelectedAsset({ label: '', value: '' });
            setService('');
            setSelectedWorkGroup('');
            setSelectedEmployee('');
            setSelectedTaskCode([]);
            setRequestor('');
            setRequestDetails('');
            setAssetDescription('');
            setTaskDescription('');
            setSelectedUserDepartment('');
            setDeptDescription('');
            clearForm();
        }
    };


    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
        setSelectedAsset({ label: '', value: '' });
        setService('');
        setSelectedWorkGroup('');
        setSelectedEmployee('')
        setSelectedTaskCode([]);
        setRequestor('');
        setRequestDetails('');
        setSelectedEmployee([]);
        setAssetDescription('');
        setTaskDescription('');
        setSelectedUserDepartment('');
        setDeptDescription('');
        clearForm();
    };
    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        setOpen(false);
        setSelectedAsset({ label: '', value: '' });
        setService('');
        setSelectedWorkGroup('');
        setSelectedEmployee('')
        setSelectedTaskCode([]);
        setRequestor('');
        setRequestDetails('');
        setSelectedEmployee([]);
        setAssetDescription('');
        setTaskDescription('');
        setSelectedUserDepartment('');
        setDeptDescription('');
        clearForm();
    };


    const handleSubmit = () => {
        let formIsValid = true;
        const newErrors = {
            service: false,
            selectedUserDepartment: false,
            selectedAsset: false,
            selectedTaskCode: false,
            selectedWorkGroup: false,
            requestor: false,
            selectedEmployee: false,
            requestDetails: false
        };
        const newErrorMessages = {
            service: "",
            selectedUserDepartment: "",
            selectedAsset: "",
            selectedTaskCode: "",
            selectedWorkGroup: "",
            requestor: "",
            selectedEmployee: "",
            requestDetails: ""
        };

        if (!service) {
            formIsValid = false;
            newErrors.service = true;
            newErrorMessages.service = "Service is required";
        }
        if (!selectedUserDepartment.id) {
            formIsValid = false;
            newErrors.selectedUserDepartment = true;
            newErrorMessages.selectedUserDepartment = "User Department is required";
        }
        if (!selectedAsset.id) {
            formIsValid = false;
            newErrors.selectedAsset = true;
            newErrorMessages.selectedAsset = "Asset No is required";
        }
        if (!selectedTaskCode.id) {
            formIsValid = false;
            newErrors.selectedTaskCode = true;
            newErrorMessages.selectedTaskCode = "Task Code is required";
        }
        if (!selectedWorkGroup) {
            formIsValid = false;
            newErrors.selectedWorkGroup = true;
            newErrorMessages.selectedWorkGroup = "Work Group is required";
        }
        if (!requestor) {
            formIsValid = false;
            newErrors.requestor = true;
            newErrorMessages.requestor = "Requestor is required";
        }
        if (!selectedEmployee.id) {
            formIsValid = false;
            newErrors.selectedEmployee = true;
            newErrorMessages.selectedEmployee = "Employee is required";
        }
        if (!requestDetails) {
            formIsValid = false;
            newErrors.requestDetails = true;
            newErrorMessages.requestDetails = "Request Details is required";
        }
        setErrors(newErrors);
        setErrorMessages(newErrorMessages);

        if (formIsValid) {
            handleSave();
            clearForm();
        }
    }

    const handleSave = async () => {
        const serviceType = service.value === 'FEMS' ? 'fem' : 'bem';
        const checkGraph = service.value === 'FEMS' ? FemsGraph : BemsGraph;
        try {

            const payload = {
                token: token,
                facilityid: facilityid,
                requestdetails: requestDetails,
                [`${serviceType}assetregisterId`]: selectedAsset.id,
                requestor: requestor,
                [`${serviceType}taskdetailsId`]: selectedTaskCode.id,
                employeeid: selectedEmployee.id,
            };

            const workOrderresponse = await axios.post(
                `${checkGraph}/api/QuickWorkorder`,
                payload,
                config
            );
            setDialogOpen(true);
            setWorkOrderNo(workOrderresponse.data);
        } catch (error) {
            console.error("Error saving quick workorder:", error);
        }
    }

    const handleDialogClose = () => {
        setDialogOpen(false);
        handleCancel();
        clearForm();
    };
    return (
        <Box>
            <PageTitle title={"Maintenance Calendar"} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="Maintenance Calendar Tabs">
                    <Tab label="Scheduled" sx={{ fontWeight: 'bold' }} />
                    <Tab label="Unscheduled" sx={{ fontWeight: 'bold' }} />
                </Tabs>
                {access?.canCreate && <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateNew}
                >
                    + Create Quick WO
                </Button>}
            </Box>

            {tabValue === 0 && <Scheduled />}
            {tabValue === 1 && <Unscheduled />}

            {/* Modal Popup */}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>
                    <Typography sx={{ fontWeight: "bold", fontSize: "17px" }}>Create Quick WO</Typography>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="Requestor"
                                id="requestor"
                                name="requestor"
                                value={requestor}
                                onChange={handleRequestorChange}
                                isMandatoryField={true}
                                error={errors.requestor}
                                errorMessage={errorMessages.requestor}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextFieldWithSearch
                                label="User Department"
                                id="userdepartment"
                                value={selectedUserDepartment}
                                options={userDepartmentOptions.map(option => ({
                                    label: option.label,
                                    value: option.value,
                                    id: option.id
                                }))}
                                onChange={handleUserDepartmentChange}
                                isMandatoryField={true}
                                error={errors.selectedUserDepartment}
                                errorMessage={errorMessages.selectedUserDepartment}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="Department Description"
                                id="deptdesc"
                                name="deptdesc"
                                value={deptDescription}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomSelectInput
                                id="Service"
                                label="Service"
                                options={serviceOptions}
                                value={service}
                                onChange={handleServiceChange}
                                placeholder="Select Service"
                                disablePortal={true} // Add this prop
                                isMandatoryField={true}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                error={errors.service}
                                errorMessage={errorMessages.service}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomSelectInput
                                id="WorkGroup"
                                label="Work Group"
                                options={workgroupOptions}
                                value={selectedWorkGroup}
                                onChange={handleWorkGroupChange}
                                placeholder="Select Work Group"
                                disablePortal={true} // Add this prop
                                isMandatoryField={true}
                                error={errors.selectedWorkGroup}
                                errorMessage={errorMessages.selectedWorkGroup}
                                isOptionEqualToValue={(option, value) =>
                                    option?.value === value?.value
                                }
                                getOptionLabel={(option) => option.label || ""}
                            />
                        </Grid>

                        <Grid item xs={6} md={6}>
                            <CustomTextFieldWithSearch
                                isMandatoryField={true}
                                label="Asset No."
                                id="assetno"
                                value={selectedAsset}
                                options={assetOptions.map(option => ({
                                    label: `${option.label} - ${option.value}`,
                                    value: option.value,
                                    id: option.id
                                }))}
                                onChange={handleAssetChange}
                                isDisable={false}
                                error={errors.selectedAsset}
                                errorMessage={errorMessages.selectedAsset}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="Asset Description"
                                id="description"
                                name="description"
                                value={assetDescription}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextFieldWithSearch
                                label={"Task Code"}
                                id={"taskCode"}
                                name={"taskCode"}
                                onChange={handleTaskCodeChange}
                                value={selectedTaskCode}
                                options={taskCodeOptions.map(option => ({
                                    label: `${option.label} - ${option.value}`,
                                    value: option.value,
                                    id: option.id
                                }))}
                                isMandatoryField={true}
                                error={errors.selectedTaskCode}
                                errorMessage={errorMessages.selectedTaskCode}

                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="Task Description"
                                id="taskdescription"
                                name="taskDescription"
                                value={taskDescription}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextFieldWithSearch
                                isMandatoryField={true}
                                label="Employee Name"
                                id="employeename"
                                name="employeename"
                                value={selectedEmployee}
                                options={employeeOptions.map(option => ({
                                    label: option.label,
                                    id: option.id,
                                    value: option.employeename
                                }))}
                                onChange={handleEmployeeChange}
                                error={errors.selectedEmployee}
                                errorMessage={errorMessages.selectedEmployee}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextArea
                                label="Request Details"
                                id="requestDetails"
                                isMandatoryField={true}
                                value={requestDetails}
                                onChange={handleRequestDetailsChange}
                                error={errors.requestDetails}
                                helperText={errorMessages.requestDetails}
                            />
                        </Grid>

                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} variant='outlined'>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant='contained'>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                onConfirm={handleDialogClose}
                confirmBtn="Ok"
                content={`${workOrderNo} has been Created Successfully.`}
            />
            <CustomDialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleCancelDialogConfirm}
                onCancel={handleCancelDialogClose}
            />
        </Box>
    );
};

export default MaintenanceCalender;
